/**
 * DocLineageInspector — DAT-2 + DAT-3 (Document Applicability Transparency).
 *
 * Doc-first lineage dialog. Opened from `DocApplicabilityChips`
 * "Show detail" button. Answers four customer questions on one
 * surface:
 *
 *   1. Why is this doc applicable to those report types?
 *      → "Solves" table — intent / fulfilled / DCT rows with score +
 *      rationale, sourced from `applicableRequirements`.
 *   2. What specific SED variables did this doc populate?
 *      → "Populates" table — driven by `buildReverseIndex` lookups,
 *      enriched with the per-section `linkedDocumentRefs` row keyed
 *      to this doc (excerpt + page + score).
 *   3. How do I validate the extraction?
 *      → "[Open at page]" and "[Verify]" buttons on every populated
 *      row. Both call the `onOpenAtPage` prop; the parent
 *      (DocumentLibrary) wires this to the existing DocViewerDialog
 *      `initialPage` channel.
 *   4. Why is this doc more important than the other 80 on the site?
 *      → "Importance" panel — every term of the
 *      `describeDocImportance` blend with its raw value, weight, and
 *      contribution. Rank within the site is rendered next to the
 *      total.
 *
 * SED is loaded LAZILY when the dialog opens (`useEffect` triggers a
 * `loadSedById` against the site's stored sedId/storagePath); the
 * inspector renders an empty Populates table with a "loading SED…"
 * placeholder while the fetch is in flight. This keeps the
 * DocumentLibrary row render path fast — no extra Firestore reads
 * fire on the 95% of sites whose users never click "Show detail".
 *
 * Risk register coverage:
 *   - R3 (highlight fails on scanned PDFs) — we don't pass a highlight
 *     parameter (DocViewerDialog has no public highlightText prop yet);
 *     the inspector exposes the excerpt below the row so the user can
 *     ctrl-F the literal sentence inside the PDF viewer if the text
 *     layer is present. Future work: wire a real highlightText channel.
 *   - R5 (disclosure overwhelm) — sections are scrollable; the Solves
 *     and Populates panels collapse when empty.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import type { SiteDocument } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument, SedSectionId, LinkedDocumentRef } from '../../types/siteEvidence';
import {
  applicableRequirements,
  loadDctScoreMapForDoc,
  type ApplicabilityReason,
  type DctScoreEntry,
} from '../../lib/docApplicability';
import {
  PRIORITY_BOOST,
  RECENCY_WINDOW_DAYS,
  REQUIREMENT_SATURATION,
  SLOT_SATURATION,
  WEIGHT_MAX_DCT,
  WEIGHT_RECENCY,
  WEIGHT_REQS_SERVED,
  WEIGHT_SLOTS_POPULATED,
  describeDocImportance,
  rankDocsByImportanceMap,
} from '../../lib/docImportance';
import { buildReverseIndex } from '../../lib/docSedReverseIndex';
import {
  buildFieldFocusId,
  groupConflictsByField,
  type FieldConflictGroup,
} from '../../lib/conflictGrouping';
import type { EvidenceConflict } from '../../services/evidenceConflict';

interface Props {
  doc: SiteDocument | null;
  site: SiteEntity | undefined;
  open: boolean;
  onClose: () => void;
  /** Opens DocViewerDialog at the given page for the supplied doc.
   *  Wired by DocumentLibrary to `setViewerDoc(doc)` + `setViewerInitialPage(page)`. */
  onOpenAtPage?: (doc: SiteDocument, page?: number) => void;
  /**
   * DF-3 — cross-doc conflict resolver deep-link. Called when the user
   * clicks "Resolve in UFC →" on a cross-doc conflict row. Convention
   * matches the existing UFC focus param shape (sectionId + fieldKey).
   * Wired by DocumentLibrary → SiteLandingPage to flip the active tab
   * to Site Evidence and set `?focus=variable:<sectionId>.<fieldKey>`,
   * which the EvidenceConflictsPanel already scrolls + ring-highlights.
   * Optional — when omitted the resolve button is hidden (legacy
   * mounts / test harnesses without a parent router don't render a
   * dead button).
   */
  onViewVariable?: (sectionId: string, fieldKey: string) => void;
}

/* ── DF-3 cross-doc conflict helpers ─────────────────────────────── */

/** One cross-doc conflict row shown on the doc-lineage inspector. */
export interface CrossDocConflictRow {
  /** Stable key (`<sectionId>.<fieldKey>`) — also the UFC focus token. */
  key: string;
  sectionId: string;
  fieldKey: string;
  fieldLabel: string;
  /** THIS doc's extracted value for the field (always present — we
   *  only surface rows where THIS doc is part of the disagreement). */
  thisDocValue: unknown;
  /** Every OTHER doc in the group whose extracted value disagrees
   *  with this doc's (post-uniqueness collapse on `docValue`). */
  disagreeingDocs: Array<{
    docId: string;
    docName: string;
    docValue: unknown;
  }>;
  worstSeverity: FieldConflictGroup['worstSeverity'];
}

/**
 * Walks the post-UFC grouping and emits one row per
 * `(sectionId, fieldKey)` group in which:
 *   (a) THIS doc is a member, AND
 *   (b) at least one OTHER doc is also a member, AND
 *   (c) at least one of those other docs disagrees on `docValue`.
 *
 * Exported so the test suite can pin the projection without
 * rendering React. Same reason `groupConflictsByField` is exported
 * standalone — pure projections that share a key shape stay testable
 * in isolation.
 */
export function buildCrossDocConflictRows(
  docId: string,
  conflicts: readonly EvidenceConflict[] | null | undefined,
  documents?: readonly SiteDocument[],
): CrossDocConflictRow[] {
  if (!conflicts || conflicts.length === 0) return [];
  // Pre-filter the same way EvidenceConflictsPanel does: skip
  // already-resolved (user OR auto) rows so we don't surface noise the
  // panel hides. The doc-lineage inspector is a doc-first surface;
  // resolved disagreements don't need a second resolution prompt here.
  const live = conflicts.filter(c => !c.userResolution && !c.autoResolved);
  const groups = groupConflictsByField(live, documents);
  const rows: CrossDocConflictRow[] = [];
  for (const g of groups) {
    const thisMember = g.members.find(m => m.docId === docId);
    if (!thisMember) continue; // THIS doc isn't in the group
    // Collect the OTHER docs whose values disagree with this one.
    // Equality is Object.is for primitives + JSON-string fallback for
    // objects (mirror EvidenceConflictsPanel's `formatValue` →
    // string-based comparison so the UI agrees with the grouper on
    // what "disagrees" means).
    const isDifferent = (other: unknown): boolean => {
      if (Object.is(thisMember.docValue, other)) return false;
      try {
        return JSON.stringify(thisMember.docValue) !== JSON.stringify(other);
      } catch {
        // Cycles / non-serialisable values → treat as disagreement to
        // surface the issue rather than silently dropping it.
        return true;
      }
    };
    const disagreeingDocs = g.members
      .filter(m => m.docId && m.docId !== docId && isDifferent(m.docValue))
      .map(m => ({ docId: m.docId, docName: m.docName, docValue: m.docValue }));
    if (disagreeingDocs.length === 0) continue;
    rows.push({
      key: g.key,
      sectionId: g.sectionId,
      fieldKey: g.fieldKey,
      fieldLabel: g.fieldLabel,
      thisDocValue: thisMember.docValue,
      disagreeingDocs,
      worstSeverity: g.worstSeverity,
    });
  }
  return rows;
}

/** Best-effort short rendering for the cross-doc conflict cells.
 *  Separate from `formatSlotValue` because we want to surface "null"
 *  and "undefined" verbatim here (the conflict IS that one doc
 *  recorded nothing while the other recorded a value). */
function formatConflictValue(value: unknown, maxLen = 60): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '""';
    return trimmed.length > maxLen ? `${trimmed.slice(0, maxLen - 1)}…` : trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const json = JSON.stringify(value);
    if (!json) return '—';
    return json.length > maxLen ? `${json.slice(0, maxLen - 1)}…` : json;
  } catch {
    return '—';
  }
}

const SEVERITY_CHIP: Record<FieldConflictGroup['worstSeverity'], string> = {
  high:   'bg-danger/10 text-danger border-danger/40',
  medium: 'bg-warning/10 text-warning border-warning/40',
  low:    'bg-state-hover text-text-muted border-border',
};

const REASON_LABEL: Record<ApplicabilityReason, string> = {
  intent: 'User intent',
  fulfilled: 'Fulfilled',
  'dct-match': 'Classifier match',
};

/** Best-effort short rendering of a slot value. Surface-only — no
 *  business logic depends on the format. */
function formatSlotValue(value: unknown, maxLen = 80): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value.length > maxLen ? `${value.slice(0, maxLen - 1)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const json = JSON.stringify(value);
    if (!json) return '—';
    return json.length > maxLen ? `${json.slice(0, maxLen - 1)}…` : json;
  } catch {
    return '—';
  }
}

/** Extract this doc's linkedDocumentRef from a slot if present. */
function findDocRef(slot: unknown, docId: string): LinkedDocumentRef | undefined {
  if (!slot || typeof slot !== 'object') return undefined;
  const refs = (slot as { linkedDocumentRefs?: LinkedDocumentRef[] }).linkedDocumentRefs;
  if (!Array.isArray(refs)) return undefined;
  return refs.find(r => r.docId === docId);
}

function getSlot(sed: SiteEvidenceDocument | undefined, sectionId: SedSectionId): unknown {
  if (!sed) return undefined;
  return (sed as unknown as Record<string, unknown>)[sectionId];
}

function getSlotData(sed: SiteEvidenceDocument | undefined, sectionId: SedSectionId): unknown {
  const slot = getSlot(sed, sectionId);
  if (!slot || typeof slot !== 'object') return undefined;
  return (slot as { data?: unknown }).data;
}

interface SlotRow {
  sectionId: SedSectionId;
  /** Value summary string. */
  value: string;
  pageNumber?: number;
  excerptPreview?: string;
  score?: number;
}

function buildSlotRows(
  docId: string,
  sed: SiteEvidenceDocument | undefined,
): SlotRow[] {
  if (!sed) return [];
  const reverseIndex = buildReverseIndex(sed);
  const sections = reverseIndex.get(docId) ?? [];
  return sections.map(sectionId => {
    const ref = findDocRef(getSlot(sed, sectionId), docId);
    return {
      sectionId,
      value: formatSlotValue(getSlotData(sed, sectionId)),
      pageNumber: ref?.pageNumber,
      excerptPreview: ref?.excerptPreview,
      score: ref?.score,
    };
  });
}

function formatPct(score: number): string {
  return `${Math.round(score * 100)}%`;
}

function formatNumber(n: number): string {
  return n.toFixed(2);
}

export const DocLineageInspector: React.FC<Props> = ({ doc, site, open, onClose, onOpenAtPage, onViewVariable }) => {
  const [sed, setSed] = useState<SiteEvidenceDocument | undefined>(undefined);
  const [sedLoading, setSedLoading] = useState(false);
  const [sedError, setSedError] = useState<string | null>(null);

  // DMS-3 — lazy DM2 cache load. The doc-lineage dialog is already
  // an async surface (it `loadSedById` on mount), so we add a second
  // fan-out load that fetches the per-`(doc, requirementId)` DM2
  // cache. The map drives the strict Tier 3 path with real
  // rationale + evidence quotes. Renders nothing visible while the
  // load is in flight — the legacy projection is shown as
  // placeholder.
  const [dctScoreMap, setDctScoreMap] = useState<Map<string, DctScoreEntry> | undefined>(undefined);
  const [dctLoading, setDctLoading] = useState(false);

  useEffect(() => {
    if (!open || !doc) {
      setDctScoreMap(undefined);
      return;
    }
    let cancelled = false;
    setDctLoading(true);
    (async () => {
      try {
        const map = await loadDctScoreMapForDoc(doc);
        if (cancelled) return;
        setDctScoreMap(map);
      } catch {
        if (!cancelled) setDctScoreMap(new Map());
      } finally {
        if (!cancelled) setDctLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, doc]);

  // Lazy SED load when the dialog opens for a doc on a site with a
  // stored SED pointer. Tolerant of: no site, no sedId, fetch failure.
  useEffect(() => {
    if (!open || !doc || !site) {
      setSed(undefined);
      setSedError(null);
      return;
    }
    const sedId = (site as { sedId?: string }).sedId;
    const sedStoragePath = (site as { sedStoragePath?: string }).sedStoragePath;
    if (!sedId) {
      setSed(undefined);
      setSedError(null);
      return;
    }
    let cancelled = false;
    setSedLoading(true);
    setSedError(null);
    (async () => {
      try {
        const { loadSedById } = await import('../../services/sedLoader');
        const payload = await loadSedById(sedId, sedStoragePath);
        if (cancelled) return;
        setSed(payload ?? undefined);
        if (!payload) setSedError('No SED payload available for this site yet.');
      } catch (e) {
        if (cancelled) return;
        setSedError((e as Error)?.message ?? 'Failed to load SED.');
        setSed(undefined);
      } finally {
        if (!cancelled) setSedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, doc, site]);

  // DMS-3 — once the DM2 cache map has loaded, the inspector renders
  // the strict (DM2-cache-aware) Tier-3 path. Before the map lands,
  // we fall back to the DMS-1-filtered legacy projection so the
  // inspector still has rows to show.
  const rows = useMemo(
    () => (doc ? applicableRequirements(doc, dctScoreMap ? { dctScoreMap } : undefined) : []),
    [doc, dctScoreMap],
  );
  const slotRows = useMemo(() => (doc ? buildSlotRows(doc.id, sed) : []), [doc, sed]);
  // DF-3 — cross-doc conflicts where THIS doc disagrees with another
  // doc populating the same SED slot. Surfaced under the Populates
  // table; each row deep-links to the UFC group via `onViewVariable`.
  const crossDocConflicts = useMemo(
    () => doc ? buildCrossDocConflictRows(doc.id, sed?.evidenceConflicts, site?.documents) : [],
    [doc, sed?.evidenceConflicts, site?.documents],
  );
  const importance = useMemo(
    () => (doc ? describeDocImportance(doc, sed) : null),
    [doc, sed],
  );
  const rank = useMemo(() => {
    if (!doc || !site?.documents) return null;
    const map = rankDocsByImportanceMap(site.documents, sed);
    const entry = map.get(doc.id);
    if (!entry) return null;
    return { rank: entry.rank, total: site.documents.length };
  }, [doc, site, sed]);

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        data-testid={`doc-lineage-inspector-${doc.id}`}
      >
        <DialogHeader>
          <DialogTitle className="text-sm font-bold">
            Document lineage — {doc.fileName}
          </DialogTitle>
        </DialogHeader>

        {/* Header — uploaded + rank */}
        <div className="text-[11px] text-text-secondary flex flex-wrap items-center gap-3 mt-1">
          <span data-testid={`doc-lineage-${doc.id}-uploaded`}>
            Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
          </span>
          {rank && (
            <span data-testid={`doc-lineage-${doc.id}-rank`}>
              Importance rank: <strong>{rank.rank}</strong> of {rank.total}
            </span>
          )}
        </div>

        {/* ── Solves table ───────────────────────────────────────── */}
        <section className="mt-4" data-testid={`doc-lineage-${doc.id}-solves`}>
          <h3 className="text-xs uppercase tracking-widest text-text-muted mb-2">
            Solves these requirements
            {dctLoading && (
              <span
                className="ml-2 text-[10px] text-text-muted lowercase tracking-normal italic"
                data-testid={`doc-lineage-${doc.id}-dct-loading`}
              >
                refining classifier matches…
              </span>
            )}
          </h3>
          {rows.length === 0 ? (
            <p className="text-[11px] text-text-muted">No requirement matches — this doc has no explicit intent / fulfilment record and no classifier score above threshold.</p>
          ) : (
            <table className="w-full text-[11px] border border-border">
              <thead className="bg-state-hover text-text-muted">
                <tr>
                  <th className="px-2 py-1 text-left">Requirement</th>
                  <th className="px-2 py-1 text-left">Reason</th>
                  <th className="px-2 py-1 text-right">Score</th>
                  <th className="px-2 py-1 text-left">Rationale &amp; evidence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr
                    key={r.requirementId}
                    className="border-t border-border-subtle"
                    data-testid={`doc-lineage-${doc.id}-req-${r.requirementId}`}
                    data-reason={r.reasonSource}
                  >
                    <td className="px-2 py-1 font-semibold">{r.label}</td>
                    <td className="px-2 py-1">{REASON_LABEL[r.reasonSource]}</td>
                    <td className="px-2 py-1 text-right font-mono">{formatPct(r.score)}</td>
                    <td className="px-2 py-1 text-text-secondary">
                      <div data-testid={`doc-lineage-${doc.id}-req-${r.requirementId}-rationale`}>
                        {r.rationale}
                      </div>
                      {r.evidenceQuotes && r.evidenceQuotes.length > 0 && (
                        <ul
                          className="mt-1 list-disc pl-3 text-[10px] text-text-muted"
                          data-testid={`doc-lineage-${doc.id}-req-${r.requirementId}-quotes`}
                        >
                          {r.evidenceQuotes.map((q, i) => (
                            <li key={i}>&ldquo;<em>{q}</em>&rdquo;</li>
                          ))}
                        </ul>
                      )}
                      {r.confidence && (
                        <div className="mt-1 type-body uppercase tracking-wider text-text-muted">
                          DM2 confidence: <span
                            data-testid={`doc-lineage-${doc.id}-req-${r.requirementId}-confidence`}
                          >{r.confidence}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* ── Populates table (SED slots) ─────────────────────────── */}
        <section className="mt-4" data-testid={`doc-lineage-${doc.id}-populates`}>
          <h3 className="text-xs uppercase tracking-widest text-text-muted mb-2">
            Populates these SED variables
          </h3>
          {sedLoading ? (
            <p className="text-[11px] text-text-muted">Loading SED…</p>
          ) : sedError ? (
            <p className="text-[11px] text-warning" data-testid={`doc-lineage-${doc.id}-sed-error`}>
              {sedError}
            </p>
          ) : slotRows.length === 0 ? (
            <p className="text-[11px] text-text-muted">
              No SED slots populated by this document. The doc may be classified for the report type but not have contributed extracted facts to any section yet.
            </p>
          ) : (
            <table className="w-full text-[11px] border border-border">
              <thead className="bg-state-hover text-text-muted">
                <tr>
                  <th className="px-2 py-1 text-left">SED section</th>
                  <th className="px-2 py-1 text-left">Value</th>
                  <th className="px-2 py-1 text-right">Page</th>
                  <th className="px-2 py-1 text-left">Excerpt</th>
                  <th className="px-2 py-1 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {slotRows.map(s => (
                  <tr
                    key={s.sectionId}
                    className="border-t border-border-subtle"
                    data-testid={`doc-lineage-${doc.id}-slot-${s.sectionId}`}
                  >
                    <td className="px-2 py-1 font-mono">{s.sectionId}</td>
                    <td className="px-2 py-1 text-text-secondary">{s.value}</td>
                    <td className="px-2 py-1 text-right font-mono">
                      {typeof s.pageNumber === 'number' ? s.pageNumber : '—'}
                    </td>
                    <td className="px-2 py-1 italic text-text-muted">
                      {s.excerptPreview ? `"${s.excerptPreview}"` : '—'}
                    </td>
                    <td className="px-2 py-1">
                      {onOpenAtPage && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => onOpenAtPage(doc, s.pageNumber)}
                            className="text-[10px] text-primary underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                            data-testid={`doc-lineage-${doc.id}-open-${s.sectionId}`}
                          >
                            Open at page
                          </button>
                          <button
                            type="button"
                            onClick={() => onOpenAtPage(doc, s.pageNumber)}
                            disabled={!s.excerptPreview}
                            className="text-[10px] text-primary underline disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                            title={s.excerptPreview
                              ? 'Opens the viewer at the page; ctrl-F the excerpt to verify.'
                              : 'No excerpt recorded for this section.'}
                            data-testid={`doc-lineage-${doc.id}-verify-${s.sectionId}`}
                          >
                            Verify
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* ── DF-3 Cross-doc conflicts ────────────────────────────── */}
        {crossDocConflicts.length > 0 && (
          <section
            className="mt-4"
            data-testid={`doc-lineage-${doc.id}-cross-doc-conflicts`}
          >
            <h3 className="text-xs uppercase tracking-widest text-text-muted mb-2">
              Cross-doc conflicts
            </h3>
            <p className="text-[10px] text-text-muted mb-2">
              Other docs claim to populate the same SED slot with a different
              value. Click <span className="font-semibold">Resolve in UFC</span>{' '}
              to pick which value wins.
            </p>
            <table className="w-full text-[11px] border border-border">
              <thead className="bg-state-hover text-text-muted">
                <tr>
                  <th className="px-2 py-1 text-left">SED slot</th>
                  <th className="px-2 py-1 text-left">This doc&rsquo;s value</th>
                  <th className="px-2 py-1 text-left">Disagreeing docs</th>
                  <th className="px-2 py-1 text-left">Severity</th>
                  <th className="px-2 py-1 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {crossDocConflicts.map(row => {
                  // Defensive: the focus id we hand to onViewVariable MUST
                  // match the EvidenceConflictsPanel's group key shape.
                  // We re-derive via the same helper so a future
                  // FOCUS_DELIMITER change can't drift the two surfaces.
                  const focusId = buildFieldFocusId(row.sectionId, row.fieldKey);
                  return (
                    <tr
                      key={row.key}
                      className="border-t border-border-subtle"
                      data-testid={`doc-lineage-${doc.id}-conflict-${row.key}`}
                    >
                      <td className="px-2 py-1 font-mono">{row.sectionId}.{row.fieldKey}</td>
                      <td className="px-2 py-1 text-text-secondary">
                        <span data-testid={`doc-lineage-${doc.id}-conflict-${row.key}-this-value`}>
                          {formatConflictValue(row.thisDocValue)}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <ul
                          className="space-y-0.5"
                          data-testid={`doc-lineage-${doc.id}-conflict-${row.key}-others`}
                        >
                          {row.disagreeingDocs.map(o => (
                            <li key={o.docId} className="text-text-secondary">
                              <span className="text-text-muted">{o.docName}:</span>{' '}
                              <span data-testid={`doc-lineage-${doc.id}-conflict-${row.key}-other-${o.docId}`}>
                                {formatConflictValue(o.docValue)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-2 py-1">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 type-body uppercase tracking-wider border ${SEVERITY_CHIP[row.worstSeverity]}`}
                          data-testid={`doc-lineage-${doc.id}-conflict-${row.key}-severity`}
                        >
                          {row.worstSeverity}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        {onViewVariable ? (
                          <button
                            type="button"
                            onClick={() => onViewVariable(row.sectionId, row.fieldKey)}
                            className="text-[10px] text-primary underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                            data-testid={`doc-lineage-${doc.id}-conflict-${row.key}-resolve`}
                            data-focus-id={focusId}
                            title={`Open the Site Evidence tab with the conflict for ${row.fieldLabel} highlighted.`}
                          >
                            Resolve in UFC →
                          </button>
                        ) : (
                          <span className="text-[10px] text-text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {/* ── Importance breakdown ────────────────────────────────── */}
        {importance && (
          <section className="mt-4" data-testid={`doc-lineage-${doc.id}-importance`}>
            <h3 className="text-xs uppercase tracking-widest text-text-muted mb-2">Importance breakdown</h3>
            <table className="w-full text-[11px] border border-border">
              <thead className="bg-state-hover text-text-muted">
                <tr>
                  <th className="px-2 py-1 text-left">Term</th>
                  <th className="px-2 py-1 text-right">Raw value</th>
                  <th className="px-2 py-1 text-right">Weight</th>
                  <th className="px-2 py-1 text-right">Contribution</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border-subtle" data-testid={`doc-lineage-${doc.id}-imp-dct`}>
                  <td className="px-2 py-1">Max DCT relevance (across report types)</td>
                  <td className="px-2 py-1 text-right font-mono">{formatPct(importance.maxDct)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(WEIGHT_MAX_DCT)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.contributions.dct)}</td>
                </tr>
                <tr className="border-t border-border-subtle" data-testid={`doc-lineage-${doc.id}-imp-reqs`}>
                  <td className="px-2 py-1">
                    Requirements served{' '}
                    <span className="text-text-muted">(saturates at {REQUIREMENT_SATURATION})</span>
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{importance.reqsServed}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(WEIGHT_REQS_SERVED)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.contributions.reqs)}</td>
                </tr>
                <tr className="border-t border-border-subtle" data-testid={`doc-lineage-${doc.id}-imp-slots`}>
                  <td className="px-2 py-1">
                    SED slots populated{' '}
                    <span className="text-text-muted">(saturates at {SLOT_SATURATION})</span>
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{importance.slotsPopulated}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(WEIGHT_SLOTS_POPULATED)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.contributions.slots)}</td>
                </tr>
                <tr className="border-t border-border-subtle" data-testid={`doc-lineage-${doc.id}-imp-recency`}>
                  <td className="px-2 py-1">
                    Recency boost{' '}
                    <span className="text-text-muted">(linear decay over {RECENCY_WINDOW_DAYS} days)</span>
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.recency)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(WEIGHT_RECENCY)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.contributions.recency)}</td>
                </tr>
                <tr className="border-t border-border-subtle" data-testid={`doc-lineage-${doc.id}-imp-priority`}>
                  <td className="px-2 py-1">User priority star</td>
                  <td className="px-2 py-1 text-right font-mono">{importance.priority ? 'yes' : 'no'}</td>
                  <td className="px-2 py-1 text-right font-mono">+{formatNumber(PRIORITY_BOOST)}</td>
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.contributions.priority)}</td>
                </tr>
                <tr className="border-t border-border bg-state-hover font-bold" data-testid={`doc-lineage-${doc.id}-imp-total`}>
                  <td className="px-2 py-1">Importance index (clamped to 1)</td>
                  <td className="px-2 py-1" />
                  <td className="px-2 py-1" />
                  <td className="px-2 py-1 text-right font-mono">{formatNumber(importance.importance)}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-[10px] text-text-muted mt-2">
              Weights are visible constants in <code className="font-mono">src/lib/docImportance.ts</code>. If a rank looks wrong, the math is auditable line-by-line.
            </p>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
};
